/**
 * OTRACK — AutoCAD object-snap tracking (acquired-point alignment paths).
 * Pure, THREE-free, store-free geometry leaf. Sibling of
 * {@link ./polarConstraint} and {@link ./orthoConstraint}.
 *
 * Distinct from POLAR tracking: POLAR projects rays from the current draft
 * anchor (`$LASTPOINT`); OTRACK projects rays from one or more explicitly
 * ACQUIRED object-snap points (hover-dwell or the `TT` command keyword), and
 * does not require a draft anchor at all — it can engage on the very first
 * point of a command. When two acquired points' alignment paths cross near
 * the cursor, BOTH engage simultaneously and the cursor snaps to their
 * intersection (AutoCAD "combined tracking").
 *
 * # Angle set (Autodesk OTRACK / POLARMODE reference)
 *
 * Every acquired point ALWAYS contributes the four cardinal directions
 * (0/90/180/270 — AutoCAD's "orthogonal alignment paths"). When POLARMODE
 * bit 2 ("object snap tracking uses the polar tracking angles") is set, the
 * live POLAR angle table (increment / additional angles / measurement
 * basis) is folded in too — {@link otrackAlignmentAngles}.
 *
 * # Resolution priority
 *
 * 1. The nearest INTERSECTION of two rays from two DIFFERENT acquired
 *    points, if within `apertureWorld` of the cursor (combined tracking).
 * 2. Else the nearest SINGLE ray (any acquired point, any angle) within
 *    `apertureWorld` — cursor perpendicular-projects onto it.
 * 3. Else no engagement — cursor returned verbatim.
 *
 * References:
 *   - Autodesk: "Track to Points Not on an Object" / "Use Object Snap
 *     Tracking" — orthogonal + polar alignment paths from acquired points,
 *     combinable tracking from ≥2 acquired points.
 *   - Autodesk POLARMODE System Variable — bit 2 (OTRACK uses polar
 *     tracking angles), bit 8 (press SHIFT to acquire).
 */

import {
  buildPolarAngleTable,
  norm360,
  type PolarAngleBasis,
  type PolarTrackingConfig,
} from "./polarConstraint";

export interface OtrackPoint {
  readonly x: number;
  readonly y: number;
}

/** One acquired object-snap tracking point. Typed array element — never a lone boolean. */
export interface OtrackAcquiredPoint {
  /** Stable id (mint order). Used for de-dup / removal, never displayed. */
  readonly id: string;
  /**
   * The acquired location. In the STORE this is a DOCUMENT coordinate (see
   * `state/otrackAcquisitionStore.ts`'s frame note); by the time it reaches
   * this module's pure math, `otrackDraftLock` has already mapped it into
   * the ACTIVE INPUT SPACE the cursor is expressed in, so everything here —
   * including every `CandidateRay.acquiredWorld` — is active-space.
   */
  readonly world: OtrackPoint;
  /**
   * OTRACK-SUBSYSTEM (local Codex review round 5 P2) — the input space
   * (`ActiveInputSpace.spaceId`) `world` was captured in. In MSPACE_VIA_VP a
   * paper/layout-space point and a model-space point are DIFFERENT
   * coordinate systems with no shared numeric meaning; without this field a
   * point acquired outside the active viewport (paper space) gets
   * misinterpreted as a raw model coordinate the moment the cursor moves
   * inside a floating viewport, producing a wrong marker position and bogus
   * alignment rays. Consumers filter to `spaceId === <current emit space>`
   * before using an acquired point for alignment or marker projection
   * ({@link resolveOtrackAlignment}'s callers, never this pure function
   * itself — see `otrackDraftLock.ts`).
   */
  readonly spaceId: string;
}

/** Default combined/single-ray pull tolerance, in WORLD units (caller supplies aperture-derived value). */
export const DEFAULT_OTRACK_THRESHOLD_WORLD = 0;

/**
 * Resolve the alignment-path angle table (degrees, [0,360), sorted unique)
 * for OTRACK. Always includes the four cardinal directions; folds in the
 * live POLAR angle table when `usePolarAngles` (POLARMODE bit 2) is set.
 *
 * `polarConfig`/`referenceAngleDeg` are forwarded verbatim to
 * {@link buildPolarAngleTable} — the SAME typed config POLAR tracking
 * itself uses, so OTRACK's "uses the polar tracking angles" bit really
 * does reuse the live increment / additional-angles / basis, not a
 * re-derived subset.
 */
export function otrackAlignmentAngles(
  usePolarAngles: boolean,
  polarConfig: PolarTrackingConfig,
  referenceAngleDeg = 0,
): number[] {
  const cardinal = new Set<number>([0, 90, 180, 270]);
  if (!usePolarAngles) {
    return Array.from(cardinal).sort((a, b) => a - b);
  }
  const polarTable = buildPolarAngleTable(polarConfig, referenceAngleDeg);
  const out = new Set<number>(cardinal);
  for (const a of polarTable) out.add(a);
  return Array.from(out).sort((a, b) => a - b);
}

/** Perpendicular projection of `point` onto the infinite ray `{origin, angleDeg}`. */
export function projectOntoRay(
  origin: OtrackPoint,
  angleDeg: number,
  point: OtrackPoint,
): { point: OtrackPoint; distanceAlongRay: number; perpendicularDistance: number } {
  const rad = (angleDeg * Math.PI) / 180;
  const ux = Math.cos(rad);
  const uy = Math.sin(rad);
  const dx = point.x - origin.x;
  const dy = point.y - origin.y;
  const along = dx * ux + dy * uy;
  const projX = origin.x + along * ux;
  const projY = origin.y + along * uy;
  const perp = Math.hypot(point.x - projX, point.y - projY);
  return {
    point: { x: projX, y: projY },
    distanceAlongRay: along,
    perpendicularDistance: perp,
  };
}

/**
 * Intersection of two infinite lines through `originA`/`originB` at
 * `angleA`/`angleB` degrees, or `null` when parallel (or coincident — no
 * unique crossing).
 */
export function rayIntersection(
  originA: OtrackPoint,
  angleA: number,
  originB: OtrackPoint,
  angleB: number,
): OtrackPoint | null {
  const radA = (angleA * Math.PI) / 180;
  const radB = (angleB * Math.PI) / 180;
  const ux1 = Math.cos(radA);
  const uy1 = Math.sin(radA);
  const ux2 = Math.cos(radB);
  const uy2 = Math.sin(radB);
  const denom = ux1 * uy2 - uy1 * ux2;
  if (Math.abs(denom) < 1e-9) return null; // parallel / coincident
  const dx = originB.x - originA.x;
  const dy = originB.y - originA.y;
  const t = (dx * uy2 - dy * ux2) / denom;
  return { x: originA.x + t * ux1, y: originA.y + t * uy1 };
}

/** One engaged alignment ray, for overlay rendering + the tracking tooltip. */
export interface OtrackRayHit {
  readonly acquiredId: string;
  readonly acquiredWorld: OtrackPoint;
  readonly angleDeg: number;
  /** Point on the ray nearest the resolved cursor (the dashed line's far endpoint). */
  readonly point: OtrackPoint;
  /** Signed distance from the acquired point along the ray. */
  readonly distance: number;
}

export interface OtrackResolution {
  readonly world: OtrackPoint;
  readonly engaged: boolean;
  /** 0 (no engagement), 1 (single ray), or 2 (combined intersection) entries. */
  readonly rays: readonly OtrackRayHit[];
}

const PASSTHROUGH = (world: OtrackPoint): OtrackResolution => ({
  world,
  engaged: false,
  rays: [],
});

interface CandidateRay {
  readonly acquiredId: string;
  readonly acquiredWorld: OtrackPoint;
  readonly angleDeg: number;
}

function buildCandidateRays(
  acquired: readonly OtrackAcquiredPoint[],
  angles: readonly number[],
): CandidateRay[] {
  const rays: CandidateRay[] = [];
  for (const p of acquired) {
    for (const a of angles) {
      rays.push({ acquiredId: p.id, acquiredWorld: p.world, angleDeg: a });
    }
  }
  return rays;
}

function toRayHit(ray: CandidateRay, target: OtrackPoint): OtrackRayHit {
  const proj = projectOntoRay(ray.acquiredWorld, ray.angleDeg, target);
  return {
    acquiredId: ray.acquiredId,
    acquiredWorld: ray.acquiredWorld,
    angleDeg: norm360(ray.angleDeg),
    point: target,
    distance: proj.distanceAlongRay,
  };
}

/**
 * Resolve OTRACK alignment-path engagement for one cursor sample.
 *
 * Pure: no store reads, no THREE. `apertureWorld` is the caller-resolved
 * OSNAP aperture converted to world units (mirrors how `resolveObjectSnap`
 * sizes its own aperture) — the SAME tolerance governs both the
 * single-ray pull and the combined-intersection pull, matching AutoCAD's
 * single APBOX-derived tolerance for both.
 */
export function resolveOtrackAlignment(
  acquired: readonly OtrackAcquiredPoint[],
  cursor: OtrackPoint,
  angles: readonly number[],
  apertureWorld: number,
): OtrackResolution {
  if (acquired.length === 0 || angles.length === 0 || apertureWorld <= 0) {
    return PASSTHROUGH(cursor);
  }
  const rays = buildCandidateRays(acquired, angles);

  // Local Codex review round 5 P2 ("uncapped combined-tracking pairwise
  // scan is a real perf/freeze bug") — precompute EVERY ray's projection
  // onto the cursor ONCE (shared by both passes below, so this is still a
  // single O(rays.length) pass) and prune to `nearCursor`: only rays whose
  // PERPENDICULAR distance to the cursor is within `apertureWorld`.
  //
  // This filter is LOSSLESS, not an approximation: the perpendicular
  // distance is the MINIMUM distance from the cursor to ANY point on the
  // (infinite) ray. If that minimum already exceeds `apertureWorld`, no
  // point on the ray — including any intersection with a DIFFERENT ray —
  // can be within `apertureWorld` of the cursor either, so the
  // combined-tracking loop's own `dist > apertureWorld` rejection would
  // have discarded every pair involving it regardless. Filtering first
  // just avoids computing (and discarding) those pairs at all.
  //
  // Without this, `rays.length` scales with acquired-point-count × the
  // live POLAR angle table (up to `MAX_POLAR_ANGLE_TABLE_SIZE` = 1440 when
  // POLARMODE bit 2 folds it in — see `polarConstraint.ts`), and the
  // combined-tracking scan below is O(rays.length^2): a handful of
  // acquired points could drive hundreds of millions of
  // `rayIntersection` calls per pointer move, freezing drafting. In
  // practice only a NARROW angular window around each acquired point's
  // direction-to-cursor (and its +180deg reverse, since a "ray" here is an
  // infinite line) can pass within aperture, so `nearCursor` is typically a
  // small constant per acquired point regardless of how fine POLARANG is.
  const projected = rays.map((ray) => {
    const proj = projectOntoRay(ray.acquiredWorld, ray.angleDeg, cursor);
    return { ray, point: proj.point, perpendicularDistance: proj.perpendicularDistance };
  });
  const nearCursor = projected.filter((p) => p.perpendicularDistance <= apertureWorld);

  // 1. Combined tracking — nearest intersection of two rays from DIFFERENT
  //    acquired points, within apertureWorld of the cursor. Scans ONLY the
  //    pruned `nearCursor` subset above, never the full (potentially huge)
  //    `rays` list.
  let bestIntersection: { point: OtrackPoint; a: CandidateRay; b: CandidateRay; dist: number } | null =
    null;
  for (let i = 0; i < nearCursor.length; i++) {
    for (let j = i + 1; j < nearCursor.length; j++) {
      const a = nearCursor[i]!.ray;
      const b = nearCursor[j]!.ray;
      if (a.acquiredId === b.acquiredId) continue; // same origin — not a genuine combination
      const hit = rayIntersection(a.acquiredWorld, a.angleDeg, b.acquiredWorld, b.angleDeg);
      if (!hit) continue;
      const dist = Math.hypot(hit.x - cursor.x, hit.y - cursor.y);
      if (dist > apertureWorld) continue;
      if (!bestIntersection || dist < bestIntersection.dist) {
        bestIntersection = { point: hit, a, b, dist };
      }
    }
  }
  if (bestIntersection) {
    const { point, a, b } = bestIntersection;
    return {
      world: point,
      engaged: true,
      rays: [toRayHit(a, point), toRayHit(b, point)],
    };
  }

  // 2. Single-ray pull — nearest ray (perpendicular distance) within
  //    aperture. Reuses the SAME `nearCursor` precomputation above (every
  //    ray in it already satisfies `perpendicularDistance <= apertureWorld`
  //    — equivalent to the original per-ray filter, no redundant
  //    `projectOntoRay` calls).
  let bestSingle: { ray: CandidateRay; proj: OtrackPoint; perp: number } | null = null;
  for (const p of nearCursor) {
    if (!bestSingle || p.perpendicularDistance < bestSingle.perp) {
      bestSingle = { ray: p.ray, proj: p.point, perp: p.perpendicularDistance };
    }
  }
  if (bestSingle) {
    return {
      world: bestSingle.proj,
      engaged: true,
      rays: [toRayHit(bestSingle.ray, bestSingle.proj)],
    };
  }

  return PASSTHROUGH(cursor);
}

export type { PolarAngleBasis, PolarTrackingConfig };
